import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { isDecidedCancellation, isOpenCancelStatus } from '../components/cancel/cancelFormat'
import { errorKind } from '../utils/format'
import { FREQUENT } from '../utils/polling'
import type { EtlRunCancellation, EtlRunCancellationList } from '../types/etlRunCancellation'

/**
 * Cancelling a running run: the run's requests, the one this session just made, and making one.
 *
 * THE SERVICE IS LOADED ON FIRST USE, not imported: it reaches services/api and so MSAL, which reads `window` at
 * module load, and these hooks are mounted by ExecutionDetail and the Explorer — which tests render in node with
 * only their own hooks replaced. A static import would put MSAL behind every one of those files.
 *
 * RECORDED IS NOT CANCELLED. After a request is made it is polled every {@link CANCEL_POLL_MS} until Automaton
 * decides it; the moment it is decided, every read the decision can change — Execution History, the Explorer's
 * runs, the workers, live operations, the run's request list — is invalidated, so the run is seen to stop (or not)
 * without a reload.
 */

const api = () => import('../services/etlRunCancellationsApi')

/** How often an open request is re-read: Automaton acts within about ten seconds, so a person is waiting. */
export const CANCEL_POLL_MS = 2_000

/** A run's request list. */
export function runCancellationsKey(runId: string): readonly unknown[] {
  return ['etl-run-cancellations', runId]
}

/** One request. */
export function runCancellationKey(requestId: string): readonly unknown[] {
  return ['etl-run-cancellation', requestId]
}

/**
 * Every read a decided cancel can change, as prefixes. Invalidating a key nothing holds is a no-op, so the Workers
 * page's fleet and the Explorer's runs are named whether or not they are mounted.
 */
export function cancellationInvalidationKeys(runId: string): readonly (readonly unknown[])[] {
  return [
    ['observatory', 'executions'],
    ['observatory', 'execution', runId],
    ['observatory', 'etlRuns'],
    ['observatory', 'etlRun'],
    ['observatory', 'workers'],
    ['observatory', 'liveOperations'],
    runCancellationsKey(runId),
  ]
}

function invalidateAfterDecision(queryClient: QueryClient, runId: string): void {
  for (const queryKey of cancellationInvalidationKeys(runId)) {
    void queryClient.invalidateQueries({ queryKey })
  }
}

/**
 * How often a run's request list is re-read: every {@link CANCEL_POLL_MS} while its newest request is open; on
 * FREQUENT while the run can still be cancelled (another administrator may ask); otherwise never. A 404 — an API
 * that serves no such route — and an unprovisioned table do not change by asking again.
 */
export function cancellationsRefetchInterval(
  data: EtlRunCancellationList | undefined,
  error: unknown,
  live: boolean,
): number | false {
  if (data?.latest && isOpenCancelStatus(data.latest.status)) return CANCEL_POLL_MS
  if (errorKind(error) === 'not-found') return false
  if (data && !data.provisioned) return false
  return live ? FREQUENT : false
}

/** How often the request this session made is re-read: until it is decided, then never. */
export function cancellationRefetchInterval(data: EtlRunCancellation | undefined, error: unknown): number | false {
  if (errorKind(error) === 'not-found') return false
  return isDecidedCancellation(data) ? false : CANCEL_POLL_MS
}

/**
 * A run's cancel requests, newest first. `live` is whether the run can still be cancelled (running or stale): it
 * decides whether the list keeps watching for someone else's request. Disabled with no run id.
 *
 * When the newest request goes from open to decided between two reads, the decision's reads are invalidated, exactly
 * as for a request this session made.
 */
export function useRunCancellations(runId: string, live: boolean) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: runCancellationsKey(runId),
    queryFn: async () => {
      const previous = queryClient.getQueryData<EtlRunCancellationList>(runCancellationsKey(runId))
      const next = await (await api()).getRunCancellations(runId)
      const was = previous?.latest
      if (was && isOpenCancelStatus(was.status)) {
        const now = next.items?.find((r) => r.requestId === was.requestId)
        if (isDecidedCancellation(now)) invalidateAfterDecision(queryClient, runId)
      }
      return next
    },
    enabled: Boolean(runId),
    refetchInterval: (query) => cancellationsRefetchInterval(query.state.data, query.state.error, live),
    // A 404 is an API that does not serve the route — an answer, not a fault — so it is not retried.
    retry: (failureCount, error) => errorKind(error) !== 'not-found' && failureCount < 3,
  })
}

/**
 * The request this session just made, polled every {@link CANCEL_POLL_MS} until Automaton decides it. Disabled
 * until there is one. The first read that finds it decided invalidates every read the decision can change.
 */
export function useRunCancellationRequest(requestId: string | null) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: runCancellationKey(requestId ?? ''),
    queryFn: async () => {
      const id = requestId ?? ''
      const previous = queryClient.getQueryData<EtlRunCancellation>(runCancellationKey(id))
      const next = await (await api()).getRunCancellation(id)
      if (isDecidedCancellation(next) && !isDecidedCancellation(previous)) {
        invalidateAfterDecision(queryClient, next.runId)
      }
      return next
    },
    enabled: requestId !== null && requestId !== '',
    refetchInterval: (query) => cancellationRefetchInterval(query.state.data, query.state.error),
  })
}

/**
 * Requests a cancel (bearer-only; the server requires a note). On success the answer seeds the request's own query —
 * so polling starts from what the server just said — and the run's request list is re-read; it says nothing itself:
 * the dialog shows the toast. It never claims the run stopped.
 */
export function useRequestRunCancellation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ runId, note }: { runId: string; note: string }) =>
      (await api()).requestRunCancellation(runId, note),
    onSuccess: (response: EtlRunCancellation, { runId }) => {
      queryClient.setQueryData(runCancellationKey(response.requestId), response)
      void queryClient.invalidateQueries({ queryKey: runCancellationsKey(runId) })
    },
  })
}
